import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import styled from 'styled-components'
import { ChevronLeft } from 'lucide-react'
import { format } from 'date-fns'
import { formatDateOnly } from 'utils/formatting'
import { SamplesApi } from 'services/samplesApi'
import LoadingScreen from 'components/Layout/LoadingScreen'
import { MobileColors } from 'styles/mobileTokens'
import { TextPill } from 'components/FbUI/TextPill'
import { FbLink } from 'components/FbUI/FbLink'
import { BackButton } from 'components/Mobile/Mobile.styles'

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${MobileColors.backgroundWhite};
  overflow-y: auto;
  padding-bottom: 100px;
`

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: ${MobileColors.backgroundWhite};
  border-bottom: 1px solid ${MobileColors.borderDefault};
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const HeaderLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
  margin-bottom: 4px;
`

const RequestNumber = styled.h1`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #191d23;
`

const CreatedDate = styled.div`
  font-size: 12px;
  color: #6b7280;
`

const Section = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${MobileColors.borderDefault};
`

const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 600;
  color: #191d23;
  margin: 0 0 12px 0;
`

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`

const InfoLabel = styled.span`
  font-size: 13px;
  color: #6b7280;
`

const InfoValue = styled.span`
  font-size: 13px;
  color: #191d23;
  text-align: right;
  flex: 1;
  margin-left: 16px;
`

const ProductCard = styled.div`
  background: #f9fafb;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`

const ProductName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #191d23;
  margin-bottom: 4px;
`

const ProductSku = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 4px;
`

const ProductQuantity = styled.div`
  font-size: 12px;
  color: #374151;
`

const CancelLink = styled.button`
  background: none;
  border: none;
  color: #dc2626;
  font-size: 14px;
  cursor: pointer;
  padding: 16px;
  width: 100%;
  text-align: center;

  &:hover {
    text-decoration: underline;
  }
`

export function MobileSampleDetailPage() {
  const navigate = useNavigate()
  const { sample_id } = useParams<{ sample_id: string }>()

  const { data: order, isLoading } = useQuery({
    queryKey: ['sample-request', sample_id],
    queryFn: () => SamplesApi.getSampleRequest(Number(sample_id)),
    enabled: !!sample_id,
  })

  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-'
    try {
      return format(new Date(dateString), 'MM/dd/yyyy h:mm a')
    } catch {
      return dateString
    }
  }

  if (isLoading) {
    return <LoadingScreen />
  }

  if (!order) {
    return (
      <PageContainer>
        <BackButton onClick={() => navigate('/samples')}>
          <ChevronLeft size={20} />
          Back
        </BackButton>
        <div className="p-4 text-center text-gray-500">
          Sample request not found
        </div>
      </PageContainer>
    )
  }

  const statusColors = {
    submitted: { bg: '#FEF5B4', text: '#463B05' },
    cancelled: { bg: '#FFD1C9', text: '#642509' },
  }

  const color = statusColors[order.status] || { bg: '#6B7280', text: '#FFFFFF' }

  return (
    <PageContainer>
      <BackButton onClick={() => navigate('/samples')}>
        <ChevronLeft size={20} />
        Back
      </BackButton>

      {/* Header */}
      <HeaderSection>
        <HeaderRow>
          <div>
            <HeaderLabel>Sample Request Details</HeaderLabel>
            <RequestNumber>Request #{order.request_number}</RequestNumber>
            <CreatedDate>Created {formatDateTime(order.created)}</CreatedDate>
          </div>
          <TextPill backgroundColor={color.bg} textColor={color.text}>
            {order.status_display}
          </TextPill>
        </HeaderRow>
      </HeaderSection>

      {/* Order Information */}
      <Section>
        <SectionTitle>Order Information</SectionTitle>
        <InfoRow>
          <InfoLabel>Date Needed</InfoLabel>
          <InfoValue>{formatDateOnly(order.date_needed)}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Fulfillment Option</InfoLabel>
          <InfoValue>{order.fulfillment_option_display}</InfoValue>
        </InfoRow>
        {order.distributor_name && (
          <InfoRow>
            <InfoLabel>Distributor</InfoLabel>
            <InfoValue>{order.distributor_name}</InfoValue>
          </InfoRow>
        )}
      </Section>

      {/* Contact & Company */}
      <Section>
        <SectionTitle>Contact &amp; Company</SectionTitle>
        <InfoRow>
          <InfoLabel>Company</InfoLabel>
          <InfoValue>
            <FbLink to={`/company/${order.company_id}`}>
              {order.company_name}
            </FbLink>
          </InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Contact Name</InfoLabel>
          <InfoValue>
            {order.contact_first_name} {order.contact_last_name}
          </InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Contact Phone</InfoLabel>
          <InfoValue>{order.contact_phone || '-'}</InfoValue>
        </InfoRow>
        <InfoRow>
          <InfoLabel>Requested by</InfoLabel>
          <InfoValue>
            {order.user_name} ({order.user_email})
          </InfoValue>
        </InfoRow>
      </Section>

      {/* Shipping Address */}
      {order.fulfillment_option === 'ship_to_address' && (
        <Section>
          <SectionTitle>Shipping Address</SectionTitle>
          <InfoRow>
            <InfoLabel>Address Line 1</InfoLabel>
            <InfoValue>{order.shipping_address_line1 || '-'}</InfoValue>
          </InfoRow>
          {order.shipping_address_line2 && (
            <InfoRow>
              <InfoLabel>Address Line 2</InfoLabel>
              <InfoValue>{order.shipping_address_line2}</InfoValue>
            </InfoRow>
          )}
          <InfoRow>
            <InfoLabel>City</InfoLabel>
            <InfoValue>{order.shipping_city || '-'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>State</InfoLabel>
            <InfoValue>{order.shipping_state || '-'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Zip Code</InfoLabel>
            <InfoValue>{order.shipping_zip || '-'}</InfoValue>
          </InfoRow>
          <InfoRow>
            <InfoLabel>Country</InfoLabel>
            <InfoValue>{order.shipping_country || 'US'}</InfoValue>
          </InfoRow>
        </Section>
      )}

      {/* Products */}
      <Section>
        <SectionTitle>Products</SectionTitle>
        {order.line_items && order.line_items.length > 0 ? (
          order.line_items.map((item) => (
            <ProductCard key={item.id}>
              <ProductName>
                {item.product_name}
                {item.variant_name && ` - ${item.variant_name}`}
              </ProductName>
              {item.variant_sku && (
                <ProductSku>SKU: {item.variant_sku}</ProductSku>
              )}
              <ProductQuantity>
                Quantity: {item.quantity ?? '-'}
                {item.product_format && ` (${item.product_format})`}
              </ProductQuantity>
            </ProductCard>
          ))
        ) : (
          <div className="text-gray-500">No products selected</div>
        )}
      </Section>

      {/* Special Instructions */}
      {order.special_instructions && (
        <Section>
          <SectionTitle>Special Instructions</SectionTitle>
          <div className="text-sm whitespace-pre-wrap">
            {order.special_instructions}
          </div>
        </Section>
      )}

      {/* Cancel Request Link */}
      {order.status === 'submitted' && (
        <CancelLink onClick={() => console.log('Cancel request')}>
          Cancel Request
        </CancelLink>
      )}
    </PageContainer>
  )
}
